import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  Alert, Input, Modal, Table, Typography,
} from 'antd';
import { useCreateBrewsBulk } from '../hooks';
import * as fmt from '../format';

dayjs.extend(customParseFormat);

const { Paragraph, Text } = Typography;

const COLUMNS = ['beer', 'brewery', 'litres', 'brew date', 'ready date', 'status', 'gyle', 'notes'];
const DATE_FORMATS = ['YYYY-MM-DD', 'D/M/YYYY', 'D/M/YY', 'D-M-YYYY', 'D MMM YYYY', 'D MMM YY'];
const STATUSES = Object.keys(fmt.BREW_STATUS);

const parseDate = (value) => {
  if (!value) return null;
  const d = dayjs(value.trim(), DATE_FORMATS, true);
  return d.isValid() ? d.format('YYYY-MM-DD') : undefined;
};

const findByName = (list, value) => {
  const v = String(value || '').trim().toLowerCase();
  return list.find((x) => x.toLowerCase() === v);
};

/**
 * Parse pasted rows (tab- or comma-separated, straight from Google Sheets) in COLUMNS order.
 * A first row that looks like a header is skipped.
 */
export const parsePastedBrews = (text, beerNames, breweries) => {
  const lines = String(text || '').split(/\r?\n/).filter((l) => l.trim());
  const rows = lines.map((line) => (line.includes('\t') ? line.split('\t') : line.split(',')));
  if (rows.length && /beer/i.test(rows[0][0] || '') && !findByName(beerNames, rows[0][0])) rows.shift();
  return rows.map((cells, i) => {
    const [beer, brewery, volume, brewDate, packageDate, status, gyle, notes] = cells.map((c) => (c || '').trim());
    const errors = [];
    const beerName = findByName(beerNames, beer);
    if (!beerName) errors.push(`Unknown beer "${beer}"`);
    const breweryName = findByName(breweries, brewery);
    if (!breweryName) errors.push(`Unknown brewery "${brewery}"`);
    const volumeLitres = Math.round(Number(String(volume).replace(/[,\sL]/gi, '')));
    if (!(volumeLitres > 0)) errors.push('Litres must be a number');
    const brew = parseDate(brewDate);
    if (!brew) errors.push(`Brew date "${brewDate}" not recognised`);
    const ready = parseDate(packageDate);
    if (ready === undefined) errors.push(`Ready date "${packageDate}" not recognised`);
    const statusValue = status ? status.toLowerCase() : 'planned';
    if (!STATUSES.includes(statusValue)) errors.push(`Status must be one of ${STATUSES.join(', ')}`);
    return {
      key: i,
      beerName: beerName || beer,
      brewery: breweryName || brewery,
      volumeLitres,
      brewDate: brew || null,
      packageDate: ready || null,
      status: statusValue,
      gyleCode: gyle || null,
      notes: notes || null,
      errors,
    };
  });
};

const PasteBrewsModal = ({ open, plan, onClose }) => {
  const [text, setText] = useState('');
  const createBulk = useCreateBrewsBulk();
  const beerNames = useMemo(() => plan.beers.map((b) => b.beerName), [plan]);
  const breweries = useMemo(() => (plan.settings.tanks || []).map((t) => t.brewery), [plan]);
  const rows = useMemo(
    () => parsePastedBrews(text, beerNames, breweries),
    [text, beerNames, breweries],
  );
  const invalid = rows.filter((r) => r.errors.length);

  const submit = async () => {
    await createBulk.mutateAsync(rows.map(({ key, errors, ...brew }) => brew));
    setText('');
    onClose();
  };

  return (
    <Modal
      title="Paste brews from a spreadsheet"
      visible={open}
      onCancel={onClose}
      onOk={submit}
      okText={rows.length ? `Add ${rows.length} brews` : 'Add brews'}
      okButtonProps={{ disabled: !rows.length || invalid.length > 0 }}
      confirmLoading={createBulk.isLoading}
      width={1000}
    >
      <Paragraph>
        Copy rows from Google Sheets and paste below. Columns, in order:
        {' '}
        <Text code>{COLUMNS.join(' | ')}</Text>
        . Ready date, status (default planned), gyle and notes are optional. Dates like 2026-10-05
        or 5/10/2026.
      </Paragraph>
      <Input.TextArea
        rows={6}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={'Beer\tB-Studio\t25000\t5/10/2026\nFugazi\tB-Studio\t20000\t12/10/2026'}
      />
      {invalid.length > 0 && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message={`${invalid.length} row(s) need fixing before they can be added`}
        />
      )}
      {rows.length > 0 && (
        <Table
          style={{ marginTop: 16 }}
          size="small"
          pagination={false}
          dataSource={rows}
          columns={[
            { title: 'Beer', dataIndex: 'beerName' },
            { title: 'Brewery', dataIndex: 'brewery' },
            { title: 'Litres', dataIndex: 'volumeLitres', render: fmt.number },
            { title: 'Brew date', dataIndex: 'brewDate', render: fmt.date },
            { title: 'Ready', dataIndex: 'packageDate', render: (d) => (d ? fmt.date(d) : 'auto') },
            { title: 'Status', dataIndex: 'status' },
            { title: 'Gyle', dataIndex: 'gyleCode' },
            {
              title: 'Problems',
              dataIndex: 'errors',
              render: (errors) => (errors.length ? <Text type="danger">{errors.join('; ')}</Text> : 'OK'),
            },
          ]}
        />
      )}
    </Modal>
  );
};

PasteBrewsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  plan: PropTypes.shape({
    beers: PropTypes.arrayOf(PropTypes.object),
    settings: PropTypes.shape({ tanks: PropTypes.arrayOf(PropTypes.object) }),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PasteBrewsModal;
